import { PointCloudOctree } from '@pnext/three-loader';
// PointCloudWalkAbortError is re-exported below for callsite compatibility,
// not used inside this module — see the `export { ... as ... }` near the
// top. Importing it as a value would trigger TS6133.
import { PointCloudWalkError, walkPointCloud } from '@/lib/pointcloud/walkPointCloud';
import { evalBestFitPlane, fitPlaneZxy, pointInPolygon2D, polygonAabb, type BestFitPlane } from './pointInPolygon';

/**
 * Stockpile volume estimator — thin wrapper around `walkPointCloud`.
 *
 * The shared walker handles octree traversal, on-demand node loading,
 * bounded-parallel bulk loads, chunked iteration, progress reporting, and
 * abort. This module is just the volume-specific bits:
 *
 *   - Region predicate: node's world AABB intersects the polygon AABB.
 *   - Per-point predicate: AABB pre-cull → `pointInPolygon2D` → max-Z per
 *     grid cell.
 *   - Output: integration of (max-Z minus best-fit base plane) over all
 *     inside-polygon cells.
 *
 * See `walkPointCloud.ts` for the full rationale on coordinate transforms,
 * V2-loader semantics, and why we walk `pco.pcoGeometry.root` directly
 * rather than using `pco.pick`.
 */

export interface ComputeVolumeInput {
  /** XY-projected polygon (N≥3 vertices, no closing duplicate). */
  polygonXY: Array<[number, number]>;
  /** 3-D vertices of the polygon (used to fit the base plane — Z values matter). */
  polygonXYZ: Array<[number, number, number]>;
  /** Source point cloud — we'll walk its `.pcoGeometry` directly, no rendering involved. */
  pco: PointCloudOctree;
  /** Cell size in metres — drives both accuracy and target octree depth. */
  cellSize: number;
  /**
   * Target sampling density:
   *   - 'standard' → octree node spacing ≤ cellSize (~1 point per cell on average).
   *   - 'fine'     → spacing ≤ cellSize / 4 (~16 points per cell, robust to noise / outliers).
   */
  samplingQuality?: 'standard' | 'fine';
  /** Two-phase progress callback. `phase` lets the UI label "Loading data" vs "Computing". */
  onProgress?: (phase: 'loading' | 'computing', done: number, total: number) => void;
  /** Optional cancellation signal; throws AbortError between async boundaries. */
  signal?: AbortSignal;
  /** Capture per-cell sample data for visualization. ~40 bytes/cell. */
  captureCells?: boolean;
  /**
   * How many `loader.load(node)` calls run in parallel. Mirrors the
   * walker's parameter — see `walkPointCloud.ts` for the rationale.
   */
  maxConcurrentLoads?: number;
}

export interface SampleCell {
  x: number;
  y: number;
  baseZ: number;
  topZ: number;
  dz: number;
  pointCount: number;
}

export interface ComputeVolumeResult {
  volumeM3: number;
  areaM2: number;
  meanHeightM: number;
  maxHeightM: number;
  /** Inside-polygon cells that received at least one point. */
  sampleCount: number;
  /** Inside-polygon cells with zero points — coverage holes. */
  missCount: number;
  basePlane: BestFitPlane;
  cellSize: number;
  durationMs: number;
  cells?: SampleCell[];
  /** How many octree leaf nodes were walked + iterated. Useful telemetry. */
  nodesUsed: number;
}

/**
 * Re-exported for callsite compatibility — the panel's try/catch uses these
 * names. Aliased to the shared walker's error classes so `instanceof` keeps
 * working without callsite changes.
 */
export { PointCloudWalkAbortError as StockpileComputeAbortError } from '@/lib/pointcloud/walkPointCloud';
export { PointCloudWalkError as StockpileComputeError } from '@/lib/pointcloud/walkPointCloud';

export async function computeStockpileVolume(input: ComputeVolumeInput): Promise<ComputeVolumeResult> {
  const { polygonXY, polygonXYZ, pco, cellSize, onProgress, signal } = input;
  const samplingQuality = input.samplingQuality ?? 'standard';
  const captureCells = input.captureCells === true;
  const maxConcurrentLoads = input.maxConcurrentLoads ?? 6;

  if (polygonXY.length < 3) throw new PointCloudWalkError('Polygon must have at least 3 vertices');
  if (!Number.isFinite(cellSize) || cellSize <= 0) throw new PointCloudWalkError('Cell size must be positive');

  const plane = fitPlaneZxy(polygonXYZ);
  if (!plane) throw new PointCloudWalkError('Failed to fit base plane (collinear vertices?)');

  const t0 = performance.now();
  const aabb = polygonAabb(polygonXY);

  const targetSpacing = samplingQuality === 'fine' ? cellSize / 4 : cellSize;

  const polyMinX = aabb.minX;
  const polyMaxX = aabb.maxX;
  const polyMinY = aabb.minY;
  const polyMaxY = aabb.maxY;

  // Pre-allocate the per-cell aggregation maps before the walk so the hot
  // per-point loop just hits them with `.set` / `.get`.
  const halfCell = cellSize * 0.5;
  const nx = Math.max(1, Math.ceil((aabb.maxX - aabb.minX) / cellSize));
  const ny = Math.max(1, Math.ceil((aabb.maxY - aabb.minY) / cellSize));
  if (nx > 65535 || ny > 65535) {
    throw new PointCloudWalkError(`Polygon too large for cell size ${cellSize} m (grid > 65535×65535)`);
  }
  const packKey = (ix: number, iy: number) => iy * 65536 + ix;

  const cellTopZ = new Map<number, number>();
  const cellCount = new Map<number, number>();

  const walkResult = await walkPointCloud({
    pco,
    shouldVisitNode: (worldBox) =>
      // XY culling — Z range is unbounded since we want everything above the
      // base plane (and we ignore points below in the integration anyway).
      worldBox.max.x >= polyMinX &&
      worldBox.min.x <= polyMaxX &&
      worldBox.max.y >= polyMinY &&
      worldBox.min.y <= polyMaxY,
    onPoint: (wx, wy, wz) => {
      // Cheap AABB test before the polygon predicate.
      if (wx < polyMinX || wx > polyMaxX || wy < polyMinY || wy > polyMaxY) return;
      if (!pointInPolygon2D(wx, wy, polygonXY)) return;

      const ix = Math.min(nx - 1, Math.max(0, Math.floor((wx - aabb.minX) / cellSize)));
      const iy = Math.min(ny - 1, Math.max(0, Math.floor((wy - aabb.minY) / cellSize)));
      const key = packKey(ix, iy);
      const prev = cellTopZ.get(key);
      if (prev === undefined || wz > prev) cellTopZ.set(key, wz);
      cellCount.set(key, (cellCount.get(key) ?? 0) + 1);
    },
    onProgress: onProgress
      ? (phase, done, total) => {
          // Walker emits 'loading' | 'processing'. The stockpile panel
          // labels Phase 3 as "Computing", so translate here.
          onProgress(phase === 'processing' ? 'computing' : 'loading', done, total);
        }
      : undefined,
    signal,
    targetSpacing,
    maxConcurrentLoads,
  });

  if (walkResult.nodesUsed === 0) {
    return {
      volumeM3: 0,
      areaM2: 0,
      meanHeightM: 0,
      maxHeightM: 0,
      sampleCount: 0,
      missCount: 0,
      basePlane: plane,
      cellSize,
      durationMs: performance.now() - t0,
      cells: captureCells ? [] : undefined,
      nodesUsed: 0,
    };
  }

  // ── Aggregate. Walk every inside-polygon cell so missCount surfaces
  // sparse-coverage holes meaningfully and area-summing is independent of
  // where points happened to land.

  let volumeAccum = 0;
  let heightSum = 0;
  let maxHeight = 0;
  let occupiedCells = 0;
  let emptyCells = 0;
  const sampleCells: SampleCell[] = captureCells ? [] : (undefined as unknown as SampleCell[]);

  for (let iy = 0; iy < ny; iy++) {
    const cy = aabb.minY + halfCell + iy * cellSize;
    for (let ix = 0; ix < nx; ix++) {
      const cx = aabb.minX + halfCell + ix * cellSize;
      if (!pointInPolygon2D(cx, cy, polygonXY)) continue;
      const baseZ = evalBestFitPlane(plane, cx, cy);
      const key = packKey(ix, iy);
      const topZ = cellTopZ.get(key);
      if (topZ === undefined) {
        emptyCells += 1;
        if (captureCells) {
          sampleCells.push({ x: cx, y: cy, baseZ, topZ: baseZ, dz: 0, pointCount: 0 });
        }
        continue;
      }
      const rawDz = topZ - baseZ;
      const dz = rawDz > 0 ? rawDz : 0;
      if (dz > 0) {
        volumeAccum += dz * cellSize * cellSize;
        heightSum += dz;
        if (dz > maxHeight) maxHeight = dz;
      }
      occupiedCells += 1;
      if (captureCells) {
        sampleCells.push({
          x: cx,
          y: cy,
          baseZ,
          topZ,
          dz,
          pointCount: cellCount.get(key) ?? 0,
        });
      }
    }
  }

  const totalInsideCells = occupiedCells + emptyCells;
  const meanHeight = occupiedCells > 0 ? heightSum / occupiedCells : 0;

  return {
    volumeM3: volumeAccum,
    areaM2: totalInsideCells * cellSize * cellSize,
    meanHeightM: meanHeight,
    maxHeightM: maxHeight,
    sampleCount: occupiedCells,
    missCount: emptyCells,
    basePlane: plane,
    cellSize,
    durationMs: performance.now() - t0,
    cells: captureCells ? sampleCells : undefined,
    nodesUsed: walkResult.nodesUsed,
  };
}

/**
 * Estimate the in-polygon cell count without loading any nodes. Drives the
 * compute-button gating + warnings in the panel.
 */
export function estimateCellCount(polygonXY: Array<[number, number]>, cellSize: number): number {
  if (polygonXY.length < 3 || !Number.isFinite(cellSize) || cellSize <= 0) return 0;
  const aabb = polygonAabb(polygonXY);
  const halfCell = cellSize * 0.5;
  const nx = Math.max(1, Math.ceil((aabb.maxX - aabb.minX) / cellSize));
  const ny = Math.max(1, Math.ceil((aabb.maxY - aabb.minY) / cellSize));
  let count = 0;
  for (let iy = 0; iy < ny; iy++) {
    const cy = aabb.minY + halfCell + iy * cellSize;
    for (let ix = 0; ix < nx; ix++) {
      const cx = aabb.minX + halfCell + ix * cellSize;
      if (pointInPolygon2D(cx, cy, polygonXY)) count += 1;
    }
  }
  return count;
}
